import React from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  resolveDashboardSecondaryNavigation,
  resolveDashboardToolNavigationTarget,
} from "../../utils/dashboardNavigation";
import {
  loadProjectNavigationState,
  markProjectNavigationItemExplored,
  persistProjectNavigationExploration,
  projectNavigationItemExplored,
  projectNavigationKey,
  projectNavigationUnseenToolKeys,
  usesProgressiveProjectNavigation,
} from "../../utils/progressiveNavigation";
import DashboardNavigationIcon from "./DashboardNavigationIcon";
import { WEBPROFIT_RESPONSIVE } from "../../theme/webProfitResponsive";

const ACTIVE = "#F35A3A";
const INK = "var(--wp-panel-text-primary, #19242F)";

function toolLabel(tool, compact) {
  if (compact) return tool.mobileLabel || tool.shortLabel || tool.label;
  return tool.shortLabel || tool.label;
}

function NavigationToolsRow({
  navigation,
  area,
  tools,
  compact,
  newToolKeys,
  context = false,
  contextLabel = "",
  contextStartKey = "",
  activeToolKey = "",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollerRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (!compact || !activeToolKey) return;

    const scroller = scrollerRef.current;
    const activeItem = scroller?.querySelector('[data-secondary-tool-active="true"]');
    if (!scroller || !activeItem) return;

    const frame = scroller.getBoundingClientRect();
    const item = activeItem.getBoundingClientRect();
    const visibilityPadding = 12;
    const fullyVisible = item.left >= frame.left + visibilityPadding
      && item.right <= frame.right - visibilityPadding;

    if (fullyVisible) return;

    const targetLeft = scroller.scrollLeft
      + (item.left - frame.left)
      - Math.max(0, (scroller.clientWidth - item.width) / 2);

    scroller.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: "smooth",
    });
  }, [compact, activeToolKey, location.pathname, location.search, tools]);

  if (!tools.length) return null;

  return (
    <Box
      sx={{
        minHeight: compact ? 48 : (context ? 44 : 50),
        pl: compact ? `max(8px, ${WEBPROFIT_RESPONSIVE.safeLeft})` : 2.5,
        pr: compact ? `max(8px, ${WEBPROFIT_RESPONSIVE.safeRight})` : 2.5,
        display: "flex",
        alignItems: "center",
        gap: compact ? 0.75 : 1.25,
        minWidth: 0,
        borderTop: context ? "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.055)" : 0,
        bgcolor: context ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)" : "transparent",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: compact ? "none" : { md: "none", lg: "block" },
          flex: "0 0 auto",
          color: context ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.44)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.5)",
          fontWeight: 950,
          letterSpacing: 0.55,
          textTransform: "uppercase",
        }}
      >
        {context ? contextLabel : area.label}
      </Typography>

      <Stack
        ref={scrollerRef}
        direction="row"
        alignItems="center"
        spacing={0.65}
        role="group"
        aria-label={context ? contextLabel : `Herramientas de ${area.label}`}
        tabIndex={compact ? 0 : undefined}
        sx={{
          minWidth: 0,
          flex: 1,
          width: "100%",
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          scrollSnapType: "x proximity",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          scrollPaddingInline: compact ? 12 : 0,
          touchAction: compact ? "pan-x pan-y" : "auto",
          "&::-webkit-scrollbar": { display: "none" },
          "&:focus-visible": compact ? {
            outline: "2px solid rgba(243,90,58,.24)",
            outlineOffset: -2,
            borderRadius: 2,
          } : undefined,
        }}
      >
        {tools.map((tool) => {
          const label = toolLabel(tool, compact);
          const beginsContext = compact && contextStartKey && tool.key === contextStartKey;
          const locked = Boolean(tool.locked || tool.restricted);
          const newForProject = newToolKeys.has(tool.key) && !tool.active;
          const tooltipBase = locked ? `${tool.label} · pausado por pago` : tool.label;
          const tooltip = newForProject
            ? `${tooltipBase} · todavía no la visitaste`
            : tooltipBase;

          return (
            <React.Fragment key={tool.key}>
              {beginsContext ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.65}
                  aria-hidden="true"
                  sx={{ flex: "0 0 auto", pl: 0.35 }}
                >
                  <Box sx={{ width: 1, height: 24, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.12)" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                      fontWeight: 900,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contextLabel}
                  </Typography>
                </Stack>
              ) : null}
              <Tooltip title={tooltip} arrow enterDelay={400}>
              <Button
                type="button"
                size="small"
                aria-current={tool.active ? "page" : undefined}
                aria-label={`${tool.label}${newForProject ? ", nueva para tu configuración" : ""}`}
                data-secondary-tool-key={tool.key}
                data-secondary-tool-active={tool.key === activeToolKey ? "true" : undefined}
                data-navigation-learning={newForProject ? "unseen-relevant-tool" : undefined}
                onClick={() => navigate(
                  resolveDashboardToolNavigationTarget(navigation, tool, location),
                )}
                startIcon={locked
                  ? <LockClockRoundedIcon sx={{ fontSize: "18px !important" }} />
                  : <DashboardNavigationIcon name={tool.iconKey} sx={{ fontSize: "18px !important" }} />}
                sx={{
                  position: "relative",
                  flex: "0 0 auto",
                  minHeight: compact ? WEBPROFIT_RESPONSIVE.touchTarget : (context ? 34 : 36),
                  maxWidth: compact ? "none" : 220,
                  px: compact ? 1.05 : 1.25,
                  borderRadius: 2,
                  scrollSnapAlign: "start",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  overflow: compact ? "visible" : "hidden",
                  color: tool.active ? ACTIVE : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                  bgcolor: tool.active ? "rgba(243,90,58,.09)" : "transparent",
                  border: tool.active
                    ? "1px solid rgba(243,90,58,.22)"
                    : "1px solid transparent",
                  boxShadow: tool.active ? "0 5px 16px rgba(243,90,58,.14)" : "none",
                  fontSize: compact ? ".74rem" : ".78rem",
                  fontWeight: tool.active ? 950 : 850,
                  "@keyframes wpSecondaryNavActivePop": {
                    "0%": { transform: "translateY(1px) scale(.96)", boxShadow: "0 0 0 rgba(243,90,58,0)" },
                    "55%": { transform: "translateY(-1px) scale(1.035)", boxShadow: "0 7px 20px rgba(243,90,58,.2)" },
                    "100%": { transform: "translateY(0) scale(1)", boxShadow: "0 5px 16px rgba(243,90,58,.14)" },
                  },
                  "@keyframes wpSecondaryNavShine": {
                    "0%": { opacity: 0, backgroundPosition: "145% 0" },
                    "30%": { opacity: 0.9 },
                    "100%": { opacity: 0, backgroundPosition: "-145% 0" },
                  },
                  animation: tool.active ? "wpSecondaryNavActivePop 440ms cubic-bezier(.2,.9,.25,1.15)" : "none",
                  "&::before": tool.active ? {
                    content: '""',
                    position: "absolute",
                    inset: 1,
                    borderRadius: "inherit",
                    pointerEvents: "none",
                    background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,.8) 50%, transparent 70%)",
                    backgroundSize: "250% 100%",
                    opacity: 0,
                    animation: "wpSecondaryNavShine 650ms ease-out 50ms 1",
                  } : undefined,
                  "&::after": tool.active ? {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    bottom: 2,
                    width: 22,
                    height: 2,
                    borderRadius: 999,
                    bgcolor: ACTIVE,
                    transform: "translateX(-50%)",
                    boxShadow: "0 0 8px rgba(243,90,58,.42)",
                  } : undefined,
                  "& .MuiButton-startIcon": { mr: 0.65, flex: "0 0 auto" },
                  "&:hover": {
                    color: INK,
                    bgcolor: tool.active ? "rgba(243,90,58,.12)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.055)",
                  },
                  "&.Mui-focusVisible": {
                    outline: "3px solid rgba(243,90,58,.2)",
                    outlineOffset: 1,
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                    "&::before": { animation: "none" },
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: compact ? "visible" : "hidden",
                    textOverflow: compact ? "clip" : "ellipsis",
                  }}
                >
                  {label}
                </Box>
                {newForProject ? (
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: ACTIVE,
                      boxShadow: "0 0 0 2px rgba(var(--wp-panel-surface-rgb, 255,255,255),.98)",
                    }}
                  />
                ) : null}
              </Button>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}

export default function DashboardSecondaryNavigation({
  navigation,
  compact = false,
  project = null,
  superAdmin = false,
}) {
  const { user } = useAuth();
  const secondary = React.useMemo(
    () => resolveDashboardSecondaryNavigation(navigation),
    [navigation],
  );
  const area = secondary.area;
  const tools = secondary.tools || [];
  const contextualTools = secondary.context?.tools || [];
  const contextualParent = secondary.context?.parent || null;
  const projectKey = projectNavigationKey(project);
  const userKey = String(user?.id || user?._id || user?.email || "").trim().toLowerCase();
  const progressiveEnabled = Boolean(
    navigation?.route?.isProject &&
      usesProgressiveProjectNavigation(project, { superAdmin }),
  );
  const [progressiveState, setProgressiveState] = React.useState(() =>
    loadProjectNavigationState(project, user),
  );

  React.useEffect(() => {
    setProgressiveState(loadProjectNavigationState(project, user));
  }, [projectKey, userKey, project, user]);

  const allVisibleTools = React.useMemo(
    () => [...tools, ...contextualTools],
    [tools, contextualTools],
  );
  const newToolKeys = React.useMemo(
    () => new Set(
      progressiveEnabled
        ? projectNavigationUnseenToolKeys(project, allVisibleTools, progressiveState)
        : [],
    ),
    [progressiveEnabled, project, allVisibleTools, progressiveState],
  );

  const activeToolKey = navigation?.activeLeafTool?.key
    || navigation?.activeSecondaryTool?.key
    || "";

  React.useEffect(() => {
    if (
      !progressiveEnabled ||
      !activeToolKey ||
      projectNavigationItemExplored(progressiveState, activeToolKey)
    ) return;

    setProgressiveState((current) =>
      markProjectNavigationItemExplored(project, activeToolKey, current, user),
    );
    persistProjectNavigationExploration(project, { itemKey: activeToolKey }).catch(() => {});
  }, [
    progressiveEnabled,
    projectKey,
    project,
    user,
    activeToolKey,
    progressiveState,
  ]);

  if (!area || !tools.length) return null;

  return (
    <Box
      component="nav"
      aria-label={`Herramientas de ${area.label}`}
      sx={{
        width: "100%",
        minWidth: 0,
        bgcolor: "rgba(var(--wp-panel-surface-rgb, 255,255,255),.97)",
        borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
      }}
    >
      {compact ? (
        <NavigationToolsRow
          navigation={navigation}
          area={area}
          tools={allVisibleTools}
          compact
          newToolKeys={newToolKeys}
          contextStartKey={contextualTools[0]?.key || ""}
          contextLabel={contextualParent?.shortLabel || contextualParent?.label || ""}
          activeToolKey={activeToolKey}
        />
      ) : (
        <>
          <NavigationToolsRow
            navigation={navigation}
            area={area}
            tools={tools}
            compact={false}
            newToolKeys={newToolKeys}
            activeToolKey={activeToolKey}
          />
          {contextualParent && contextualTools.length ? (
            <NavigationToolsRow
              navigation={navigation}
              area={area}
              tools={contextualTools}
              compact={false}
              newToolKeys={newToolKeys}
              context
              contextLabel={contextualParent.shortLabel || contextualParent.label}
              activeToolKey={activeToolKey}
            />
          ) : null}
        </>
      )}
    </Box>
  );
}
